import { Controller } from "@hotwired/stimulus";
import { parseNumber, formatNumberArgentina } from "../utils/number_formatter";

export default class extends Controller {
  static targets = [
    "orderSelect", "netAmount", "fileInput", "manualAmountContainer",
    "manualAmountInput", "lineItemsContainer", "lineItemsBody",
    "invoiceType", "subtotalAmount", "ivaAmount", "otrosTributosInput",
    "otrosTributosAmount", "tributosContainer", "tributoRow"
  ];

  static values = {
    supplierId: Number
  }

  // Use shared utility functions
  parseNumber = parseNumber
  formatNumberArgentina = formatNumberArgentina

  connect() {
    setTimeout(() => {
      const isServiceProvider = !this.hasOrderSelectTarget;

      if (!isServiceProvider) {
        this.initializeCalculation();
        // Load line items if there's a preselected order
        this.loadLineItems();
      }
      this.toggleSubmitButton();
    }, 100);
  }

  updateManualAmount() {
    try {
      const manualAmountInput = this.manualAmountInputTarget;
      const netAmount = this.netAmountTarget;

      if (!manualAmountInput || !netAmount) return;

      let value = manualAmountInput.value;
      value = value.replace(/[^\d,.-]/g, '');

      let numericValue = 0;
      if (value.includes(',') || value.includes('.')) {
        value = value.replace(/\./g, '').replace(',', '.');
        numericValue = parseFloat(value) || 0;
      } else {
        numericValue = parseFloat(value) || 0;
      }

      if (numericValue > 0) {
        netAmount.textContent = "$" + this.formatNumberArgentina(numericValue, 2);
      } else {
        netAmount.textContent = "$0,00";
      }

      this.toggleSubmitButton();
    } catch (e) {
      console.warn('Error updating manual amount:', e);
    }
  }

  initializeCalculation() {
    let orderSelect, netAmount;

    try {
      orderSelect = this.orderSelectTarget;
      netAmount = this.netAmountTarget;
    } catch (e) {
      setTimeout(() => this.initializeCalculation(), 100);
      return;
    }

    if (!orderSelect || !netAmount) {
      setTimeout(() => this.initializeCalculation(), 100);
      return;
    }

    const checkSlimSelect = () => {
      if (orderSelect.slimSelect || orderSelect.selectedOptions) {
        this.calculateNetAmount();
      } else {
        setTimeout(checkSlimSelect, 100);
      }
    };

    setTimeout(checkSlimSelect, 300);

    orderSelect.addEventListener('change', () => {
      this.calculateNetAmount();
      this.toggleSubmitButton();
    });
  }

  calculateNetAmount() {
    requestAnimationFrame(() => {
      let orderSelect, netAmount;

      try {
        orderSelect = this.orderSelectTarget;
        netAmount = this.netAmountTarget;
      } catch (e) {
        return;
      }

      if (!orderSelect || !netAmount) return;

      // If we have line items loaded, calculate from them instead
      if (this.hasLineItemsBodyTarget && this.lineItemsBodyTarget.children.length > 0) {
        this.calculateFromLineItems();
        return;
      }

      let selectedOptions = [];

      if (orderSelect.slimSelect) {
        const selectedValues = orderSelect.slimSelect.getSelected();
        selectedOptions = selectedValues
          .map(value => {
            const option = Array.from(orderSelect.options).find(opt => opt.value === value);
            return option;
          })
          .filter(opt => opt && opt.value);
      } else {
        selectedOptions = Array.from(orderSelect.selectedOptions || []);
      }

      let totalCents = 0;

      selectedOptions.forEach((option) => {
        const orderPriceCents = parseInt(option.getAttribute('data-order-price')) || 0;
        totalCents += orderPriceCents;
      });

      const totalPesos = totalCents / 100;

      try {
        netAmount.textContent = "$" + this.formatNumberArgentina(totalPesos, 2);
      } catch (e) {
        console.error('Error updating net amount:', e);
        netAmount.textContent = 'Error';
      }

      this.toggleSubmitButton();
    });
  }

  async loadLineItems() {
    if (!this.hasOrderSelectTarget) return;

    const orderSelect = this.orderSelectTarget;
    let selectedIds = [];

    if (orderSelect.slimSelect) {
      selectedIds = orderSelect.slimSelect.getSelected().filter(v => v && v !== '');
    } else if (orderSelect.multiple) {
      selectedIds = Array.from(orderSelect.selectedOptions || []).map(opt => opt.value).filter(v => v);
    } else {
      const val = orderSelect.value;
      if (val) selectedIds = [val];
    }

    if (selectedIds.length === 0) {
      this.hideLineItems();
      return;
    }

    try {
      const supplierId = this.supplierIdValue;
      let allLineItems = [];

      for (const poId of selectedIds) {
        const response = await fetch(`/suppliers/${supplierId}/purchase_orders/${poId}/line_items`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
          }
        });

        if (response.ok) {
          const lineItems = await response.json();
          allLineItems = allLineItems.concat(lineItems);
        }
      }

      this.renderLineItems(allLineItems);
    } catch (e) {
      console.warn('Error loading line items:', e);
    }
  }

  renderLineItems(lineItems) {
    if (!this.hasLineItemsBodyTarget || !this.hasLineItemsContainerTarget) return;

    if (lineItems.length === 0) {
      this.hideLineItems();
      return;
    }

    const tbody = this.lineItemsBodyTarget;
    tbody.innerHTML = '';

    const ivaRates = [0, 10.5, 21, 27];

    lineItems.forEach((item, index) => {
      const unitPrice = item.unit_price_cents / 100.0;
      const lineTotal = unitPrice * item.quantity;

      const row = document.createElement('tr');
      row.innerHTML = `
        <td>
          ${item.sku_name}
          <small class="text-muted d-block">${item.measurement || ''}</small>
          <input type="hidden" name="invoice[invoice_line_items_attributes][${index}][purchase_order_sku_id]" value="${item.id}">
        </td>
        <td>
          <input type="number" class="form-control form-control-sm"
                 name="invoice[invoice_line_items_attributes][${index}][invoiced_quantity]"
                 value="${item.quantity}" min="0.001" step="0.001"
                 data-action="input->supplier-invoice-form#calculateFromLineItems">
        </td>
        <td>
          <input type="text" class="form-control form-control-sm"
                 name="invoice[invoice_line_items_attributes][${index}][invoiced_price_cents]"
                 value="${this.formatNumberArgentina(unitPrice, 2)}"
                 data-action="input->supplier-invoice-form#calculateFromLineItems">
        </td>
        <td>
          <select class="form-control form-control-sm"
                  name="invoice[invoice_line_items_attributes][${index}][iva_rate]"
                  data-action="change->supplier-invoice-form#calculateFromLineItems">
            ${ivaRates.map(r => `<option value="${r}" ${parseFloat(item.iva_rate) === r ? 'selected' : ''}>${r}%</option>`).join('')}
          </select>
        </td>
        <td class="text-right line-item-subtotal">
          ${this.formatNumberArgentina(lineTotal, 2)}
        </td>
      `;
      tbody.appendChild(row);
    });

    this.lineItemsContainerTarget.classList.remove('d-none');
    this.calculateFromLineItems();
  }

  hideLineItems() {
    if (this.hasLineItemsContainerTarget) {
      this.lineItemsContainerTarget.classList.add('d-none');
    }
    if (this.hasLineItemsBodyTarget) {
      this.lineItemsBodyTarget.innerHTML = '';
    }
    // Reset subtotal and IVA displays
    if (this.hasSubtotalAmountTarget) {
      this.subtotalAmountTarget.textContent = "$0,00";
    }
    if (this.hasIvaAmountTarget) {
      this.ivaAmountTarget.textContent = "$0,00";
    }
  }

  calculateFromLineItems() {
    if (!this.hasLineItemsBodyTarget) return;

    const rows = this.lineItemsBodyTarget.querySelectorAll('tr');
    let subtotal = 0;
    let totalIva = 0;

    rows.forEach((row) => {
      const quantityInput = row.querySelector('input[name*="invoiced_quantity"]');
      const priceInput = row.querySelector('input[name*="invoiced_price_cents"]');
      const ivaSelect = row.querySelector('select[name*="iva_rate"]');
      const subtotalCell = row.querySelector('.line-item-subtotal');

      if (!quantityInput || !priceInput || !ivaSelect) return;

      const quantity = parseFloat(quantityInput.value) || 0;
      const price = this.parseNumber(priceInput.value);
      const ivaRate = parseFloat(ivaSelect.value) || 0;

      const lineTotal = quantity * price;
      const lineIva = lineTotal * (ivaRate / 100.0);

      subtotal += lineTotal;
      totalIva += lineIva;

      if (subtotalCell) {
        subtotalCell.textContent = this.formatNumberArgentina(lineTotal, 2);
      }
    });

    // Read otros tributos from tributo rows or fallback to single input
    let otrosTributos = 0;
    if (this.hasTributosContainerTarget) {
      const tributoRows = this.tributosContainerTarget.querySelectorAll('tr:not([style*="display: none"])');
      tributoRows.forEach((row) => {
        const destroyField = row.querySelector('[data-destroy-field]');
        if (destroyField && destroyField.value === "1") return;
        const amountInput = row.querySelector('.tributo-amount');
        if (amountInput) {
          otrosTributos += this.parseNumber(amountInput.value) || 0;
        }
      });
    } else if (this.hasOtrosTributosInputTarget) {
      otrosTributos = this.parseNumber(this.otrosTributosInputTarget.value) || 0;
    }

    const total = subtotal + totalIva + otrosTributos;

    if (this.hasSubtotalAmountTarget) {
      this.subtotalAmountTarget.textContent = "$" + this.formatNumberArgentina(subtotal, 2);
    }
    if (this.hasIvaAmountTarget) {
      this.ivaAmountTarget.textContent = "$" + this.formatNumberArgentina(totalIva, 2);
    }
    if (this.hasOtrosTributosAmountTarget) {
      this.otrosTributosAmountTarget.textContent = "$" + this.formatNumberArgentina(otrosTributos, 2);
    }
    if (this.hasNetAmountTarget) {
      this.netAmountTarget.textContent = "$" + this.formatNumberArgentina(total, 2);
    }

    this.toggleSubmitButton();
  }

  addTributoRow() {
    if (!this.hasTributosContainerTarget) return;

    const container = this.tributosContainerTarget;
    const index = Date.now(); // unique index for nested attributes
    const row = document.createElement('tr');
    row.setAttribute('data-supplier-invoice-form-target', 'tributoRow');
    row.innerHTML = `
      <td>
        <input type="text" class="form-control form-control-sm"
               name="invoice[invoice_tributos_attributes][${index}][description]"
               placeholder="Ej: PERCEPCION IVA CF">
        <input type="hidden" name="invoice[invoice_tributos_attributes][${index}][_destroy]" value="0" data-destroy-field>
      </td>
      <td>
        <select class="form-control form-control-sm"
                name="invoice[invoice_tributos_attributes][${index}][tributo_type]">
          <option value="percepcion_iva">Perc. IVA</option>
          <option value="percepcion_iibb">Perc. IIBB</option>
          <option value="otro">Otro</option>
        </select>
      </td>
      <td>
        <input type="text" class="form-control form-control-sm"
               name="invoice[invoice_tributos_attributes][${index}][alicuota]"
               placeholder="3,0"
               data-action="input->supplier-invoice-form#calculateTributoAmount">
      </td>
      <td>
        <div class="input-group input-group-sm">
          <div class="input-group-prepend"><span class="input-group-text">$</span></div>
          <input type="text" class="form-control form-control-sm"
                 name="invoice[invoice_tributos_attributes][${index}][base_imponible_cents]"
                 placeholder="0,00"
                 data-action="input->supplier-invoice-form#calculateTributoAmount">
        </div>
      </td>
      <td>
        <div class="input-group input-group-sm">
          <div class="input-group-prepend"><span class="input-group-text">$</span></div>
          <input type="text" class="form-control form-control-sm tributo-amount"
                 name="invoice[invoice_tributos_attributes][${index}][amount_cents]"
                 placeholder="0,00"
                 data-action="input->supplier-invoice-form#calculateFromLineItems">
        </div>
      </td>
      <td class="text-center">
        <button type="button" class="btn btn-sm btn-outline-danger"
                data-action="click->supplier-invoice-form#removeTributoRow">&times;</button>
      </td>
    `;
    container.appendChild(row);
  }

  removeTributoRow(event) {
    const row = event.target.closest('tr');
    if (!row) return;

    const destroyField = row.querySelector('[data-destroy-field]');
    const idField = row.querySelector('input[name*="[id]"]');

    if (idField && idField.value) {
      // Existing record: mark for destruction and hide
      if (destroyField) destroyField.value = "1";
      row.style.display = "none";
    } else {
      // New record: remove from DOM
      row.remove();
    }

    this.calculateFromLineItems();
  }

  calculateTributoAmount(event) {
    const row = event.target.closest('tr');
    if (!row) return;

    const alicuotaInput = row.querySelector('input[name*="[alicuota]"]');
    const baseInput = row.querySelector('input[name*="[base_imponible_cents]"]');
    const amountInput = row.querySelector('.tributo-amount');

    if (!alicuotaInput || !baseInput || !amountInput) return;

    const alicuota = this.parseNumber(alicuotaInput.value) || 0;
    const base = this.parseNumber(baseInput.value) || 0;

    if (alicuota > 0 && base > 0) {
      const amount = base * alicuota / 100.0;
      amountInput.value = this.formatNumberArgentina(amount, 2);
      this.calculateFromLineItems();
    }
  }

  toggleSubmitButton() {
    try {
      const submitButton = document.getElementById('create-invoice-submit');
      if (!submitButton) return;

      let hasFile = false;
      try {
        if (this.hasFileInputTarget) {
          const fileInput = this.fileInputTarget;
          hasFile = fileInput && fileInput.files && fileInput.files.length > 0;
        } else {
          hasFile = true;
        }
      } catch (e) {
        hasFile = true;
      }

      const isServiceProvider = !this.hasOrderSelectTarget;

      let hasValidData = false;

      if (isServiceProvider) {
        try {
          if (this.hasManualAmountInputTarget) {
            const manualAmountInput = this.manualAmountInputTarget;
            const manualValue = manualAmountInput.value.trim();
            if (manualValue) {
              const cleanValue = manualValue.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
              const numericValue = parseFloat(cleanValue) || 0;
              hasValidData = numericValue > 0;
            }
          }
        } catch (e) {
          hasValidData = false;
        }
      } else {
        try {
          const orderSelect = this.orderSelectTarget;
          if (!orderSelect) {
            hasValidData = false;
          } else {
            let selectedCount = 0;
            if (orderSelect.slimSelect) {
              const selectedValues = orderSelect.slimSelect.getSelected();
              selectedCount = selectedValues.filter(v => v && v !== '').length;
            } else {
              selectedCount = Array.from(orderSelect.selectedOptions || []).filter(opt => opt.value).length;
            }
            hasValidData = selectedCount > 0;
          }
        } catch (e) {
          hasValidData = false;
        }
      }

      submitButton.disabled = !hasValidData || !hasFile;
    } catch (e) {
      console.warn('Error toggling submit button:', e);
    }
  }
}
